import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Calendar, Loader2, Pause, Users } from "lucide-react";

import { toast } from "sonner";

import { useAtribuirBoard, useBoards } from "@/api/pipeline";
import { ROTULO_ETAPA, useProjeto, type Projeto } from "@/api/projetos";
import { usuarioAtual } from "@/api/registry";
import { BriefingSection } from "@/components/card/BriefingSection";
import { ComentariosSection, DebriefingSection } from "@/components/card/ComentariosDebriefing";
import { CronogramaSection } from "@/components/card/CronogramaSection";
import { EscopoSection, PrazosSection } from "@/components/card/EscopoPrazos";
import { RealizadoSection } from "@/components/card/RealizadoSection";
import { HistoricoSection, TimelineSection } from "@/components/card/TimelineHistorico";
import { Campo, Pill, Secao } from "@/components/card/comuns";
import { dataBR } from "@/components/card/formato";
import { TopBar } from "@/components/TopBar";
import { can, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/card/$id")({
  component: CardDetail,
});

/**
 * Card do projeto (M4).
 *
 * Era um arquivo de 2.217 linhas com tudo inline: o painel de IA que o PRD
 * v3.1 tirou daqui, dois sistemas paralelos de registro de produção, cinco
 * fases fixas em `useState` e um histórico de mentira. Agora orquestra
 * seções em arquivos próprios, todas contra a API.
 */
function CardDetail() {
  const { id } = Route.useParams();
  const role = useRole();
  const { data: projeto, isLoading, isError } = useProjeto(id);

  const podeEditar = can(role, "editarCard");
  const podeValidar = can(role, "validarProducao");
  const podeInformar = can(role, "registrarProducao");
  const podeAprovarInsight = can(role, "editarParametros");
  const eu = usuarioAtual();

  if (isLoading) {
    return (
      <>
        <TopBar title="Card" />
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando projeto…
        </div>
      </>
    );
  }

  if (isError || !projeto) {
    return (
      <>
        <TopBar title="Card" />
        <div className="p-10 text-center text-sm text-muted-foreground">
          Card não encontrado ou fora do seu escopo de visão.{" "}
          <Link to="/pipeline" className="text-primary hover:underline">
            Ver Pipeline
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title={projeto.cliente.nome} subtitle={projeto.nome} />

      <div className="border-b border-border bg-background/60 px-4 py-2.5">
        <Link
          to="/pipeline"
          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Voltar ao pipeline
        </Link>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-[1100px] space-y-4">
          <Cabecalho projeto={projeto} podeMover={can(role, "moverCard")} />

          {/*
            RF-59 — escopo de card restrito: o servidor não manda escopo,
            prazos das outras etapas, equipe nem reprogramações. A tela diz
            isso em vez de desenhar seções vazias.
          */}
          {projeto.escopoRestrito && (
            <p className="rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-[11px] text-muted-foreground">
              Seu perfil enxerga apenas a sua fase neste card. Escopo comercial, prazos das demais
              etapas, equipe e histórico de reprogramações não são exibidos.
            </p>
          )}

          {!projeto.escopoRestrito && (
            <>
              <EscopoSection projeto={projeto} podeEditar={podeEditar} />
              <PrazosSection projeto={projeto} podeEditar={podeEditar} />
            </>
          )}

          <Secao titulo="Equipe" subtitulo="papéis editáveis na parametrização">
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
              <Campo label="Coordenador" value={projeto.coordenador?.nome ?? "—"} />
              <Campo label="Comercial" value={projeto.comercial?.nome ?? "—"} />
              {projeto.equipe.map((m) => (
                <Campo key={m.id} label={m.papel} value={m.profissional.nome} />
              ))}
            </div>
            {!projeto.equipe.length && (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Users className="h-3 w-3" /> Nenhum papel adicional definido.
              </p>
            )}
          </Secao>

          <BriefingSection
            projetoId={projeto.id}
            briefing={projeto.briefing}
            podeEditar={podeEditar}
          />

          <RealizadoSection
            projetoId={projeto.id}
            podeInformar={podeInformar}
            podeValidar={podeValidar}
            profissionalId={eu?.profissionalId ?? null}
          />

          {/* Quem só vê a própria fase não recebe o cronograma do projeto. */}
          {!projeto.escopoRestrito && (
            <CronogramaSection
              projetoId={projeto.id}
              cenarioAtual={projeto.cenarioEscolhido}
              podeEditar={podeEditar}
            />
          )}

          <TimelineSection projeto={projeto} podeEditar={podeEditar} />

          {(podeEditar || podeAprovarInsight) && (
            <DebriefingSection
              projetoId={projeto.id}
              podeEditar={podeEditar}
              podeAprovar={podeAprovarInsight}
            />
          )}

          <ComentariosSection projetoId={projeto.id} />

          <HistoricoSection projetoId={projeto.id} />
        </div>
      </div>
    </>
  );
}

function Cabecalho({ projeto, podeMover }: { projeto: Projeto; podeMover: boolean }) {
  const vFinal = projeto.prazos?.vFinal;
  const urgencia = tomDoPrazo(vFinal);

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <div
        className="h-1 w-full"
        style={{ backgroundColor: projeto.coordenador?.cor ?? "var(--primary)" }}
      />
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Pill className="border-border bg-secondary text-muted-foreground">{projeto.os}</Pill>
              <Pill className="border-border bg-secondary text-muted-foreground">
                {projeto.frente}
              </Pill>
              <Pill className="border-border bg-secondary text-muted-foreground">
                {projeto.tipoProjeto}
              </Pill>
              <Pill className="border-primary/30 bg-primary/15 text-primary">
                {projeto.estagio}
              </Pill>
              {podeMover && <KanbanDoCard projeto={projeto} />}
              {projeto.pausadoEm && (
                <Pill className="border-health-purple/30 bg-health-purple/15 text-health-purple">
                  <Pause className="mr-1 h-3 w-3" /> Pausado
                </Pill>
              )}
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
              {projeto.nome}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Cliente: <span className="text-foreground">{projeto.cliente.nome}</span>
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium",
                urgencia.classe,
              )}
            >
              <Calendar className="h-3 w-3" />
              {dataBR(vFinal)}
              <span className="text-muted-foreground">·</span>
              <span>{urgencia.rotulo}</span>
            </div>
            {projeto.coordenador && (
              <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-2 py-1">
                <span
                  className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-semibold text-background"
                  style={{ backgroundColor: projeto.coordenador.cor ?? "#94A3B8" }}
                >
                  {iniciais(projeto.coordenador.nome)}
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Coordenador
                  </div>
                  <div className="text-xs font-medium text-foreground">
                    {projeto.coordenador.nome}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RF-25 — redflags do cliente */}
        {projeto.redflags.length > 0 && (
          <div className="mt-4 rounded-lg border border-health-yellow/40 bg-health-yellow/10 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-health-yellow">
              <AlertTriangle className="h-3.5 w-3.5" /> Atenção com este cliente
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {projeto.redflags.map((f) => (
                <span
                  key={f}
                  className="rounded-md bg-health-yellow/15 px-2 py-0.5 text-[11px] text-health-yellow"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* RF-24 — alerta de cascata */}
        {projeto.alertaCascata && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-health-red/40 bg-health-red/10 p-3">
            <AlertTriangle className="h-4 w-4 shrink-0 text-health-red" />
            <div className="text-[11px]">
              <span className="font-semibold text-health-red">Alerta de Cascata</span>
              <span className="text-foreground/90">
                {" "}
                — a fase{" "}
                <strong>
                  {ROTULO_ETAPA[projeto.alertaCascata.faseAtrasada] ??
                    projeto.alertaCascata.faseAtrasada}
                </strong>{" "}
                está atrasada e há{" "}
                <strong>
                  {projeto.alertaCascata.dependentes}{" "}
                  {projeto.alertaCascata.dependentes === 1
                    ? "fase dependente"
                    : "fases dependentes"}
                </strong>{" "}
                aguardando.
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

function tomDoPrazo(iso: string | null | undefined) {
  if (!iso) {
    return { classe: "bg-secondary text-muted-foreground border-border", rotulo: "sem prazo" };
  }
  const hoje = new Date().toISOString().slice(0, 10);
  const dias = Math.round(
    (Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${hoje}T00:00:00Z`)) / 86_400_000,
  );
  if (dias < 0) {
    return { classe: "bg-health-red/15 text-health-red border-health-red/30", rotulo: "vencido" };
  }
  if (dias <= 1) {
    return { classe: "bg-health-red/15 text-health-red border-health-red/30", rotulo: "urgente" };
  }
  if (dias <= 3) {
    return {
      classe: "bg-health-yellow/15 text-health-yellow border-health-yellow/30",
      rotulo: "atenção",
    };
  }
  return { classe: "bg-secondary text-muted-foreground border-border", rotulo: `em ${dias} dias` };
}

/**
 * Troca de kanban. O card entra na primeira coluna ativa do destino; pausado
 * não troca, para não sair de Pausado sem a data de reinício (RF-65).
 */
function KanbanDoCard({ projeto }: { projeto: Projeto }) {
  const boards = useBoards();
  const atribuir = useAtribuirBoard();
  if (!boards.data?.length) return null;

  return (
    <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
      Kanban
      <select
        value={projeto.boardId ?? ""}
        disabled={atribuir.isPending || Boolean(projeto.pausadoEm)}
        title={projeto.pausadoEm ? "Retome o projeto antes de trocá-lo de kanban" : undefined}
        onChange={(e) =>
          atribuir.mutate(
            { cardId: projeto.id, boardId: e.target.value },
            {
              onSuccess: () => toast.success("Card movido de kanban"),
              onError: (erro) => toast.error(erro.message),
            },
          )
        }
        className="h-6 rounded-md border border-border bg-card px-1.5 text-[11px] text-foreground"
      >
        {!projeto.boardId && <option value="">— sem kanban —</option>}
        {boards.data.map((b) => (
          <option key={b.id} value={b.id}>
            {b.nome}
          </option>
        ))}
      </select>
    </label>
  );
}
